import { useState, useRef } from 'react';
import Modal from '@cloudscape-design/components/modal';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Alert from '@cloudscape-design/components/alert';
import Select from '@cloudscape-design/components/select';
import Table from '@cloudscape-design/components/table';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import type { AssetCategory, Asset } from '@/types/storageInventory';

interface BulkImportModalProps {
  visible: boolean;
  onImport: (category: AssetCategory, assets: Omit<Asset, 'id'>[]) => void;
  onDismiss: () => void;
}

function parseCSV(text: string): Record<string, string>[] {
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  if (lines.length < 2) return [];

  const headers = parseCSVLine(lines[0]);
  const rows: Record<string, string>[] = [];

  for (let i = 1; i < lines.length; i++) {
    const values = parseCSVLine(lines[i]);
    const row: Record<string, string> = {};
    headers.forEach((h, idx) => {
      row[h.trim()] = (values[idx] || '').trim();
    });
    rows.push(row);
  }
  return rows;
}

function parseCSVLine(line: string): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      result.push(current);
      current = '';
    } else {
      current += char;
    }
  }
  result.push(current);
  return result;
}

const CATEGORY_OPTIONS = [
  { label: 'Servers/Encoders/Appliances', value: 'servers' },
  { label: 'Spare Elementals', value: 'elementals' },
  { label: 'Network Devices', value: 'network_devices' },
];

const EXPECTED_HEADERS: Record<AssetCategory, string[]> = {
  servers: ['site', 'hostname', 'make', 'model', 'service_tag', 'status'],
  elementals: ['site', 'series', 'manufacturer', 'model', 'service_tag', 'boxed_status', 'condition', 'status'],
  network_devices: ['site', 'make', 'model', 'serial_num', 'status', 'quantity'],
};

const MAX_BACKUPS = 5;

/** Save a backup of current data to localStorage before import */
function saveBackup(category: AssetCategory, currentData: string) {
  const backupKey = `storage-inventory-backup-${category}`;
  const existing = JSON.parse(localStorage.getItem(backupKey) || '[]') as { timestamp: string; data: string }[];
  existing.unshift({ timestamp: new Date().toISOString(), data: currentData });
  // Keep only last 5 backups
  const trimmed = existing.slice(0, MAX_BACKUPS);
  localStorage.setItem(backupKey, JSON.stringify(trimmed));
}

export function BulkImportModal({ visible, onImport, onDismiss }: BulkImportModalProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [category, setCategory] = useState<AssetCategory>('servers');
  const [parsedRows, setParsedRows] = useState<Record<string, string>[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string>('');
  const [confirmVisible, setConfirmVisible] = useState(false);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setFileName(file.name);
    setError(null);

    const reader = new FileReader();
    reader.onload = () => {
      const text = reader.result as string;
      const rows = parseCSV(text);

      if (rows.length === 0) {
        setError('No data rows found in the CSV file. Make sure it has a header row and at least one data row.');
        setParsedRows([]);
        return;
      }

      const headers = Object.keys(rows[0]);
      const required = EXPECTED_HEADERS[category];
      const missing = required.filter((h) => !headers.includes(h));
      if (missing.length > 0) {
        setError(`Missing required columns: ${missing.join(', ')}. Expected at minimum: ${required.join(', ')}`);
        setParsedRows([]);
        return;
      }

      setParsedRows(rows);
    };
    reader.readAsText(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleImportClick = () => {
    if (parsedRows.length === 0) return;
    setConfirmVisible(true);
  };

  const handleConfirmImport = () => {
    // Save backup of current data before importing
    const dataPath = `/data/storage-inventory/${category === 'network_devices' ? 'network_devices' : category}.json`;
    fetch(dataPath)
      .then((res) => res.ok ? res.text() : '[]')
      .then((currentData) => {
        saveBackup(category, currentData);
      })
      .catch(() => {});

    const assets = parsedRows.map((row) => {
      const asset: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(row)) {
        if (key === 'quantity') {
          asset[key] = parseInt(value) || 1;
        } else if (key === 'cpu_quantity' || key === 'ram_quantity' || key === 'disk_quantity') {
          asset[key] = value ? parseInt(value) : undefined;
        } else if (key === 'cost') {
          asset[key] = value ? parseFloat(value) : undefined;
        } else if (key === 'to_be_decommissioned') {
          asset[key] = value.toLowerCase() === 'true' || value === '1' || value.toLowerCase() === 'yes';
        } else {
          asset[key] = value || undefined;
        }
      }
      return asset as Omit<Asset, 'id'>;
    });

    onImport(category, assets);
    setConfirmVisible(false);
    setParsedRows([]);
    setFileName('');
    setError(null);
  };

  const handleClose = () => {
    setParsedRows([]);
    setFileName('');
    setError(null);
    setConfirmVisible(false);
    onDismiss();
  };

  const previewColumns = parsedRows.length > 0
    ? Object.keys(parsedRows[0]).slice(0, 6).map((key) => ({
        id: key,
        header: key,
        cell: (item: Record<string, string>) => item[key] || '-',
      }))
    : [];

  return (
    <>
      <Modal
        visible={visible && !confirmVisible}
        onDismiss={handleClose}
        header="Bulk Import from CSV"
        size="max"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={handleClose}>Cancel</Button>
              <Button variant="primary" onClick={handleImportClick} disabled={parsedRows.length === 0}>
                Import {parsedRows.length} row{parsedRows.length !== 1 ? 's' : ''}
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <div>
          <SpaceBetween size="l">
            <Alert type="warning">
              <strong>⚠️ This action adds data to the inventory.</strong> A backup of the current data will be saved automatically (last 5 backups kept). To undo, check localStorage backups.
            </Alert>

            <Alert type="info">
              <SpaceBetween size="xs">
                <Box variant="p"><strong>How to bulk import via CSV:</strong></Box>
                <Box variant="p">1. Go to the category tab you want to add to</Box>
                <Box variant="p">2. Click "Export to CSV" to download the current data with correct column headers</Box>
                <Box variant="p">3. Open the CSV, add new rows at the bottom (keep the header row)</Box>
                <Box variant="p">4. Come back here, select the same category, and upload the modified CSV</Box>
                <Box variant="p">5. Review the preview, then click "Import" to add all new rows</Box>
                <Box variant="p"><strong>Required columns by category:</strong></Box>
                <Box variant="p">• Servers: site, hostname, make, model, service_tag, status</Box>
                <Box variant="p">• Elementals: site, series, manufacturer, model, service_tag, boxed_status, condition, status</Box>
                <Box variant="p">• Network Devices: site, make, model, serial_num, status, quantity</Box>
              </SpaceBetween>
            </Alert>

            <SpaceBetween direction="horizontal" size="m">
              <div style={{ minWidth: 250 }}>
                <Select
                  selectedOption={CATEGORY_OPTIONS.find((o) => o.value === category) || null}
                  onChange={({ detail }) => {
                    setCategory(detail.selectedOption.value as AssetCategory);
                    setParsedRows([]);
                    setError(null);
                  }}
                  options={CATEGORY_OPTIONS}
                />
              </div>
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,text/csv"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />
                <Button iconName="upload" onClick={() => fileInputRef.current?.click()}>
                  {fileName || 'Choose CSV file'}
                </Button>
              </div>
            </SpaceBetween>

            {error && <Alert type="error">{error}</Alert>}

            {parsedRows.length > 0 && (
              <SpaceBetween size="s">
                <StatusIndicator type="success">
                  {parsedRows.length} row{parsedRows.length !== 1 ? 's' : ''} parsed from {fileName}
                </StatusIndicator>
                <Box variant="h4">Preview (first 6 columns shown):</Box>
                <Table
                  columnDefinitions={previewColumns}
                  items={parsedRows.slice(0, 10)}
                  variant="embedded"
                  stripedRows
                />
                {parsedRows.length > 10 && (
                  <Box variant="small" color="text-body-secondary">
                    ...and {parsedRows.length - 10} more rows
                  </Box>
                )}
              </SpaceBetween>
            )}
          </SpaceBetween>
        </div>
      </Modal>

      {/* Confirmation modal */}
      <Modal
        visible={confirmVisible}
        onDismiss={() => setConfirmVisible(false)}
        header="⚠️ Confirm Bulk Import"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setConfirmVisible(false)}>Cancel</Button>
              <Button variant="primary" onClick={handleConfirmImport}>
                Yes, import {parsedRows.length} rows
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="m">
          <Alert type="error">
            <strong>You are about to import {parsedRows.length} new asset{parsedRows.length !== 1 ? 's' : ''} into {category.replace('_', ' ')}.</strong>
          </Alert>
          <Box variant="p">This will:</Box>
          <Box variant="p">• Add {parsedRows.length} new row{parsedRows.length !== 1 ? 's' : ''} to the <strong>{category.replace('_', ' ')}</strong> category</Box>
          <Box variant="p">• A backup of the current data will be saved to localStorage</Box>
          <Box variant="p">• This action cannot be easily undone from the UI</Box>
          <Box variant="p"><strong>Are you sure you want to proceed?</strong></Box>
        </SpaceBetween>
      </Modal>
    </>
  );
}
